import { useCallback, useEffect, useRef, useMemo } from "react";
import { tpslRoiBasis } from "./tpslRoiBasis";
import { GetAccount200ResponsePositionsInner, SideResponse } from "../../codegen-api";
import { FormValidatorKeysEnum } from "../../enums/form";
import { TpslTriggersView } from "../../hooks/tpsl/tpsl.selectors";
import {
  slRoiName,
  slTriggerPriceName,
  tpRoiName,
  tpTriggerPriceName,
} from "./TPSLForPositionModal.helpers";
import type { TpslPriceSource } from "../../hooks/tpsl/tpsl.types";
import { Tpsl } from "./TPSLForPositionModal.types";

interface UseTPSLSyncParams {
  position?: GetAccount200ResponsePositionsInner;
  /** Live triggers (executor list merged with the position enrichment). */
  triggers: TpslTriggersView;
  markPrice: any;
  /**
   * The price the trigger will actually fire on, per the "Trigger by" selector.
   * "last" = last traded price; "oracle" = index price. Validation must use the
   * SAME reference the executor will — see referencePrice below.
   */
  priceSource?: TpslPriceSource;
  indexPrice?: any;
  lastPrice?: any;
  trigger: any;
  clearErrors: any;
  tpTriggerPrice: any;
  slTriggerPrice: any;
  tpRoi: any;
  slRoi: any;
  tpType: typeof Tpsl[keyof typeof Tpsl];
  slType: typeof Tpsl[keyof typeof Tpsl];
  setValue: any;
  contractPriceStep: any;
  triggerPriceFromRoi: (roi: number) => number | undefined;
}

export function useTPSLSync({
  position,
  triggers,
  markPrice,
  priceSource,
  indexPrice,
  lastPrice,
  trigger,
  clearErrors,
  tpTriggerPrice,
  slTriggerPrice,
  tpRoi,
  slRoi,
  tpType,
  slType,
  setValue,
  contractPriceStep,
  triggerPriceFromRoi,
}: UseTPSLSyncParams) {
  /**
   * Validate against the SELECTED trigger source, not always mark.
   *
   * This compared every trigger to `markPrice` regardless of the "Trigger by"
   * choice. Index tracks mark closely so the bug stayed hidden there, but with
   * "Last Price" selected in a thin market the two diverge — e.g. mark $1,911
   * vs last $1,579 — and a perfectly valid long TP at $1,600 was rejected for
   * being "below mark" while the executor would have fired it correctly off
   * last. Falls back to mark when the source has no live price.
   */
  const referencePrice = useMemo(() => {
    const chosen = priceSource === "last" ? lastPrice : indexPrice;
    const n = Number(chosen);
    return Number.isFinite(n) && n > 0 ? chosen : markPrice;
  }, [priceSource, lastPrice, indexPrice, markPrice]);

  const triggerPriceValidateFn = useCallback(
    (isTakeProfit: boolean) => {
      // If BUY STOP LOSS or SELL TAKE PROFIT, trigger price must be higher than mark
      if (
        (position?.side === SideResponse.Buy && isTakeProfit) ||
        (position?.side === SideResponse.Sell && !isTakeProfit)
      ) {
        return {
          [FormValidatorKeysEnum.triggerPriceBelowMark]: () => true,
          [FormValidatorKeysEnum.triggerPriceAboveMark]: (v: string) =>
            !v || parseFloat(v) >= Number(referencePrice || 0),
        };
      }
      // If SELL STOP LOSS or BUY TAKE PROFIT, trigger price must be lower than mark
      return {
        [FormValidatorKeysEnum.triggerPriceBelowMark]: (v: string) =>
          !v || parseFloat(v) <= Number(referencePrice || 0),
        [FormValidatorKeysEnum.triggerPriceAboveMark]: () => true,
      };
    },
    [referencePrice, position?.side]
  );

  // Trigger updates when inputs change
  useEffect(() => {
    clearErrors();
    trigger(tpTriggerPriceName);
    trigger(slTriggerPriceName);
    trigger(tpRoiName);
    trigger(slRoiName);
  }, [
    trigger,
    tpTriggerPrice,
    slTriggerPrice,
    tpRoiName,
    slRoiName,
    clearErrors,
  ]);

  // Bidirectional sync: when user edits Price, compute Gain; when user edits
  // Gain, compute Price. The lastEdited ref prevents infinite loops.
  const lastEditedTp = useRef<"price" | "roi" | null>(null);
  const lastEditedSl = useRef<"price" | "roi" | null>(null);

  const priceToGain = useCallback(
    (price: number, mode: typeof Tpsl[keyof typeof Tpsl]) => {
      if (!position?.avg_entry_price || !position?.amount) return undefined;
      const entry = Number(position.avg_entry_price);
      const amount = Number(position.amount);
      const sign = position.side === SideResponse.Buy ? 1 : -1;
      const pnlUsd = (price - entry) * amount * sign;
      if (mode === Tpsl.Roi) {
        // The margin the position posts, as everywhere else in the app.
        const basis = tpslRoiBasis(position);
        if (basis === undefined) return undefined;
        return (pnlUsd / basis) * 100;
      }
      return pnlUsd;
    },
    [position]
  );

  const gainToPrice = useCallback(
    (gain: number, mode: typeof Tpsl[keyof typeof Tpsl]) => {
      if (mode === Tpsl.Roi) return triggerPriceFromRoi(gain);
      if (!position?.avg_entry_price || !position?.amount) return undefined;
      const entry = Number(position.avg_entry_price);
      const amount = Number(position.amount);
      if (amount === 0) return undefined;
      const sign = position.side === SideResponse.Buy ? 1 : -1;
      return entry + (gain / amount) * sign;
    },
    [position?.avg_entry_price, position?.amount, position?.side, triggerPriceFromRoi]
  );

  // TP Price → Gain
  useEffect(() => {
    if (lastEditedTp.current !== "price") return;
    const priceNum = parseFloat(tpTriggerPrice);
    if (!Number.isFinite(priceNum) || tpTriggerPrice === "" || tpTriggerPrice == null) {
      setValue(tpRoiName, undefined as any, { shouldDirty: false, shouldValidate: false });
      return;
    }
    const computed = priceToGain(priceNum, tpType);
    if (computed == null || !Number.isFinite(computed)) return;
    setValue(tpRoiName, Number(computed.toFixed(2)) as any, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [tpTriggerPrice, tpType, priceToGain, setValue]);

  // TP Gain → Price
  useEffect(() => {
    if (lastEditedTp.current !== "roi") return;
    const gainNum = parseFloat(tpRoi);
    if (!Number.isFinite(gainNum) || tpRoi === "" || tpRoi == null) {
      setValue(tpTriggerPriceName, undefined as any, { shouldDirty: false, shouldValidate: false });
      return;
    }
    const computed = gainToPrice(gainNum, tpType);
    if (computed == null || !Number.isFinite(computed)) return;
    setValue(tpTriggerPriceName, Number(computed.toFixed(contractPriceStep.price_precision)) as any, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [tpRoi, tpType, gainToPrice, setValue, contractPriceStep.price_precision]);

  // SL Price → Loss
  useEffect(() => {
    if (lastEditedSl.current !== "price") return;
    const priceNum = parseFloat(slTriggerPrice);
    if (!Number.isFinite(priceNum) || slTriggerPrice === "" || slTriggerPrice == null) {
      setValue(slRoiName, undefined as any, { shouldDirty: false, shouldValidate: false });
      return;
    }
    const computed = priceToGain(priceNum, slType);
    if (computed == null || !Number.isFinite(computed)) return;
    setValue(slRoiName, Number(computed.toFixed(2)) as any, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [slTriggerPrice, slType, priceToGain, setValue]);

  // SL Loss → Price
  useEffect(() => {
    if (lastEditedSl.current !== "roi") return;
    const lossNum = parseFloat(slRoi);
    if (!Number.isFinite(lossNum) || slRoi === "" || slRoi == null) {
      setValue(slTriggerPriceName, undefined as any, { shouldDirty: false, shouldValidate: false });
      return;
    }
    const computed = gainToPrice(lossNum, slType);
    if (computed == null || !Number.isFinite(computed)) return;
    setValue(slTriggerPriceName, Number(computed.toFixed(contractPriceStep.price_precision)) as any, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [slRoi, slType, gainToPrice, setValue, contractPriceStep.price_precision]);

  // Prefill from the live trigger. Note this never CLEARS the field when a
  // trigger disappears: `positions[].triggers` is absent (not null) whenever
  // the executor is briefly unreachable, so absence must not be read as a
  // cancellation.
  useEffect(() => {
    if (triggers.take_profit?.trigger) {
      setValue(tpTriggerPriceName, triggers.take_profit.trigger);
    }
  }, [triggers.take_profit?.trigger, setValue]);

  useEffect(() => {
    if (triggers.stop_loss?.trigger) {
      setValue(slTriggerPriceName, triggers.stop_loss.trigger);
    }
  }, [triggers.stop_loss?.trigger, setValue]);

  useEffect(() => {
    const value = parseFloat(slRoi);
    if (!Number.isNaN(value) && value > 0) {
      // If the value is positive, set it to its negative counterpart
      setValue(slRoiName, -Math.abs(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slRoi]);

  return { triggerPriceValidateFn, lastEditedTp, lastEditedSl };
}
